
import { PickupCorporateScheduleConfigController } from "@/app/ui/controllers/PickupCorporateScheduleConfigController";
import { PickupScheduleController } from "@/app/ui/controllers/PickupScheduleController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { ApiRequestList } from "@/domain/entities/MainApp";
import { PickupCorporateAddress } from "@/domain/entities/PickupSchedule";
import { PickupCorporateScheduleConfig } from "@/domain/entities/PickupCorporateScheduleConfig";
import {
  mapEntityToUiRegularDays,
  mapEntityToUiSpecialDates,
  mapUiRegularDaysToEntity,
  mapUiSpecialDatesToEntity
} from "@/domain/entities/pickupCorporateScheduleConfigMappers";
import { validatePickupCorporateScheduleConfig } from "@/domain/validators/pickupCorporateScheduleValidator";
import { Options, Vue } from "vue-class-component";
import ModalV2 from "@/app/ui/components/modal-v2/index.vue";
import Modal from "@/app/ui/components/modal/index.vue";
import LpButton from "@/app/ui/components/lp-button/index.vue";
import PickupAddressSidebarCard from "./components/PickupAddressSidebarCard.vue";
import SavedAddressList from "../components/saved-address-list/index.vue";
import AddressSidebarPlaceholder from "./components/AddressSidebarPlaceholder.vue";
import CardLayout from "./components/CardLayout.vue";
import ShipmentInfoSection from "./components/ShipmentInfoSection.vue";
import SchedulePickupSection from "./components/SchedulePickupSection.vue";
import {
  createInitialRegularDays,
  RegularDaySchedule,
  SpecialDateSchedule
} from "./components/schedule-types";

@Options({
  components: {
    ModalV2,
    Modal,
    LpButton,
    PickupAddressSidebarCard,
    SavedAddressList,
    AddressSidebarPlaceholder,
    CardLayout,
    ShipmentInfoSection,
    SchedulePickupSection
  },
  watch: {
    "$route.query.client_id": function() {
      if (!this.isInternalAccount) return;
      this.selectedAddress = null;
      this.loadForClient();
    }
  }
})
export default class PickupScheduleConfig extends Vue {
  controller = PickupCorporateScheduleConfigController;
  selectedAddress: PickupCorporateAddress | null = null;
  addressLoading = false;
  isSavedAddressListOpen = false;
  isEditTerminated = false;
  slotIntervalMinutes = 60;
  clientId = 0;

  get isInternalAccount(): boolean {
    return AccountController.accountData.account_type === "internal";
  }

  get clientIdForAddress(): number {
    return this.isInternalAccount ? this.clientId : 0;
  }

  get showModalSuccess(): boolean {
    return this.controller.showModalSuccess;
  }

  set showModalSuccess(value: boolean) {
    this.controller.setShowModalSuccess(value);
  }

  get isMalaysia(): boolean {
    return AccountController.accountData.countryCode?.toUpperCase() === "MY";
  }

  get estimateAdditionalKoli(): string {
    return this.controller.config.estimateAdditionalKoli;
  }

  set estimateAdditionalKoli(value: string) {
    this.controller.setConfig({ estimateAdditionalKoli: value });
  }

  get estimateWeight(): string {
    return this.controller.config.estimateWeight;
  }

  set estimateWeight(value: string) {
    this.controller.setConfig({ estimateWeight: value });
  }

  get serviceCodes(): string[] {
    return this.controller.config.serviceCodes;
  }

  set serviceCodes(value: string[]) {
    this.controller.setConfig({ serviceCodes: value });
  }

  get regularEnabled(): boolean {
    return this.controller.config.regularEnabled;
  }

  set regularEnabled(value: boolean) {
    const regularDays =
      value && !this.controller.config.regularDays.length
        ? mapUiRegularDaysToEntity(createInitialRegularDays())
        : this.controller.config.regularDays;
    this.controller.setConfig({ regularEnabled: value, regularDays });
  }

  get regularDays(): RegularDaySchedule[] {
    return mapEntityToUiRegularDays(
      this.controller.config.regularDays,
      !this.controller.config.isEdit
    );
  }

  set regularDays(value: RegularDaySchedule[]) {
    this.controller.setConfig({ regularDays: mapUiRegularDaysToEntity(value) });
  }

  get specialEnabled(): boolean {
    return this.controller.config.specialEnabled;
  }

  set specialEnabled(value: boolean) {
    this.controller.setConfig({ specialEnabled: value });
  }

  get specialDates(): SpecialDateSchedule[] {
    return mapEntityToUiSpecialDates(this.controller.config.specialDates);
  }

  set specialDates(value: SpecialDateSchedule[]) {
    this.controller.setConfig({
      specialDates: mapUiSpecialDatesToEntity(value)
    });
  }

  get canSubmit(): boolean {
    return (
      validatePickupCorporateScheduleConfig(this.controller.config).valid &&
      Boolean(this.selectedAddress?.pcaId)
    );
  }

  get isSubmitting(): boolean {
    return this.controller.isSubmitting;
  }

  get clientName(): string {
    return this.isInternalAccount ? this.controller.config.clientName : "";
  }

  async mounted(): Promise<void> {
    document.title = `Genesis - ${this.$t("pickup.scheduleConfig.pageTitle")}`;
    this.clientId = Number(this.$route.query.client_id) || 0;
    if (this.isInternalAccount && !this.clientId) {
      this.$router.replace("/pickup/list-pickup-v2");
      return;
    }
    await this.loadForClient();
  }

  async loadForClient(): Promise<void> {
    await this.controller.loadConfig(
      this.isInternalAccount ? this.clientId : undefined
    );
    if (this.isInternalAccount) {
      await this.loadClientDetail();
    }
    await this.resolveDisplayAddress();
    this.controller.fetchPickupDatePickerConfig();
  }

  async loadClientDetail(): Promise<void> {
    try {
      await ClientManagementController.getDetailV2(this.clientId);
      const detail = ClientManagementController.detailClientManagement;
      if (
        detail &&
        detail.id === this.clientId &&
        (!this.controller.config.clientName || !this.controller.config.clientId)
      ) {
        this.controller.setConfig({
          clientName: detail.clientName,
          clientId: detail.id
        });
      }
    } catch {
      // client name tetap mengikuti response schedule config
    }
  }

  async resolveDisplayAddress(): Promise<void> {
    if (this.controller.config.address.pcaId) {
      this.selectedAddress = this.controller.config.address;
      PickupScheduleController.setSelectedAddressPickupCorporateAddress(
        this.controller.config.address
      );
      return;
    }
    await this.loadAddress();
  }

  async loadAddress(): Promise<void> {
    this.addressLoading = true;
    const params = new ApiRequestList({
      page: 1,
      limit: 50,
      isTotalData: false,
      selectAddress: true
    });
    if (this.isInternalAccount) {
      params.clientId = this.clientId;
    }
    await PickupScheduleController.getListPickupCorporateAddress(params);
    if (!this.selectedAddress?.pcaId) {
      this.selectedAddress = PickupScheduleController.selectedAddress;
    }
    this.addressLoading = false;
  }

  async onChangeAddress(): Promise<void> {
    this.isSavedAddressListOpen = true;
    await this.loadAddress();
  }

  onAddAddress(): void {
    this.isSavedAddressListOpen = true;

    const savedAddressListRef: any = this.$refs.savedAddressListRef;
    if (!savedAddressListRef) return;
    savedAddressListRef?.showModalAddAddress("add")
  }

  onSelectAddress(data: PickupCorporateAddress): void {
    this.selectedAddress = data;
    PickupScheduleController.setSelectedAddressPickupCorporateAddress(data);
    this.isSavedAddressListOpen = false;
  }

  async onAddressListRefresh(): Promise<void> {
    const params = new ApiRequestList({
      page: 1,
      limit: 50,
      isTotalData: false,
      selectAddress: true
    });
    if (this.isInternalAccount) {
      params.clientId = this.clientId;
    }
    await PickupScheduleController.getListPickupCorporateAddress(params);
    // If current selection was deleted/set-default, recompute
    const updated = PickupScheduleController.listPickupCorporateAddress.find(
      (address) => address.pcaId === this.selectedAddress?.pcaId
    );
    this.selectedAddress = updated || PickupScheduleController.selectedAddress;
  }

  async onSaveSchedule(): Promise<void> {
    if (!this.canSubmit) {
      return;
    }
    const config = new PickupCorporateScheduleConfig({
      ...this.controller.config,
      address: this.selectedAddress || new PickupCorporateAddress()
    });
    const success = await this.controller.saveConfig(config);
    if (success) {
      this.showModalSuccess = true;
    }
  }

  onCloseSuccessModal(): void {
    this.showModalSuccess = false;
    this.$router.push("/pickup/list-pickup-v2");
  }

  onBack(): void {
    this.isEditTerminated = true;
  }

  handlerClose(): void {
    this.isEditTerminated = false;
    this.$router.push("/pickup/list-pickup-v2");
  }

  handlerCancelClose(): void {
    this.isEditTerminated = false;
  }
}
